/**
 * Russian string inventory for the «работа» surface (Блок 2).
 *
 * Ф0 · пакет 0b · 0b-T1. This file is the **single source of the Block-2
 * lexicon**: every visible word for the work-unit model lands here first
 * and reaches components only through an import. The custom ESLint rule
 * `aris/no-legacy-lexeme` enforces that — `apps/web/src/i18n/**` is its
 * only allow-listed home, so the legacy lexeme (see below) may exist here
 * and nowhere else under `apps/web/src`.
 *
 * ⚠️ ЛЕКСЕМА ПРАВИЛА РАЗВЁРНУТА 07.08.2026. Устаревшее слово теперь —
 * «поручен…», а «задача» стала законной: заказчик выбрал «задача» ВЕЗДЕ,
 * и внутреннее слово приведено к экранному (`DECISIONS.md`, «Второй вид
 * работы называется „задача"»; миграция 051, `WorkKind.TASK`). Прежде
 * запрещённым было «задач…» — если этот абзац снова начнёт так утверждать,
 * значит правку откатили мимо решения.
 *
 * Why a dictionary and not inline literals: the model renames one concept
 * — прежняя единая «задача» разошлась на два вида работы, «проектную
 * работу» и «задачу», — across ~6 waves. A rename that lives
 * in one file is a diff; a rename scattered over components is an
 * archaeology exercise, and the половинчатый result is two words for one
 * thing on two screens.
 *
 * Sources, in order of authority (see CLAUDE.md «Где что лежит»):
 *   - `.planning/block-2-object-tracker/TZ.md` — редакция 2 от 30.07.2026;
 *   - `.planning/block-2-object-tracker/DECISIONS.md` — решения заказчика
 *     (приоритет над планом);
 *   - `.planning/block-2-object-tracker/PLAN.md` — «Принятая модель».
 *
 * Работа (`Work`) существует в двух видах через поле `kind`:
 *   - `PROJECT_WORK` — проектная работа внутри раздела объекта;
 *   - `TASK` — задача; на экране называется так же (решение заказчика 10 —
 *     названия меню «Моя работа» и «Задачи» оставляем — в силе и 07.08.2026
 *     подтверждено; менялась только вторая сторона, код и словарь).
 *
 * Single-locale by product design (CONTEXT FI-7 + research-frontend §9) —
 * no i18n runtime, plain typed constants.
 */
// ⚠️ `WorkKind` ИМПОРТИРУЕТСЯ ПОД ПСЕВДОНИМОМ, И ЭТО НЕ УКРАШЕНИЕ: этот же
// модуль ЭКСПОРТИРУЕТ тип с тем же именем (`type WorkKind = keyof typeof
// workKindRu`, ниже), и прямой импорт даёт TS2440 — конфликт объявлений. Взять
// значения перечисления нужно именно из общего пакета: ключи словарей ниже
// обязаны покрывать `Work.kind` целиком, а не совпадать с ним на глаз.
import {
  INTERNAL_WORK_KINDS,
  MISC_PROJECT_NAME,
  SECTION_FIELD_LABEL,
  WORK_STATUS_LABEL,
  WorkKind as WorkKindEnum,
  pluralRu,
  // Ф5 · В9.9 (Р-105) — текст отказа один с серверным; разбор у самого ключа.
  selfTaskBackdatedMessage,
} from '@aris/shared';

/**
 * Виды работы. Ключи совпадают со значениями `Work.kind`
 * на бэкенде, чтобы подпись выбиралась индексацией, а не `switch`.
 */
export const workKindRu = {
  /** `Work.kind = PROJECT_WORK` — заводится на экране объекта. */
  PROJECT_WORK: 'Работа',
  /** `Work.kind = TASK` — заводится на `/tasks`, в UI «Задача». */
  TASK: 'Задача',
} as const;

/** Множественное число видов — заголовки списков и группы в селектах. */
export const workKindPluralRu = {
  PROJECT_WORK: 'Работы',
  TASK: 'Задачи',
} as const;

/**
 * Состояния работы — **четыре**, и это закрытый набор.
 *
 * ТЗ §5.3-c и БП-5 перечисляют те же четыре для раздела; §5.4-a называет
 * первое «Бэклог» — расхождение снимается протоколом В0.5, пункт 5:
 * «Бэклог» → «Не начато», чтобы одно состояние не называлось на двух
 * экранах по-разному.
 *
 * Пятое состояние «у заказчика» было принято 29.07.2026 и **отменено
 * заказчиком 30.07.2026** (`DECISIONS.md`, раздел «Отменено заказчиком»).
 * Не возвращать без нового решения заказчика.
 *
 * «Просрочено» в этот набор не входит **по построению** — см.
 * `workFlagRu.overdue`.
 *
 * ⚠️ ЭТО ПСЕВДОНИМ `WORK_STATUS_LABEL` ИЗ `@aris/shared`, А НЕ ВТОРОЙ СЛОВАРЬ
 * (Ф3 · В3a, находка круга ревью). Прежде здесь лежала БАЙТ-В-БАЙТ копия
 * четырёх подписей, и обе стороны объявляли себя единственным источником:
 * `WORK_STATUS_LABEL` — комментарием «ИСТОЧНИК ПОДПИСИ ОДИН» (его читает
 * серверный форматтер журнала), этот файл — правилом словаря (его читают
 * экраны). Первая же редактура подписи разошлась бы: журнал говорил бы одно,
 * карточка другое, и заметили бы это не скоро.
 *
 * Псевдоним, а не удаление: правило репозитория «компоненты берут подписи из
 * `@/i18n`» остаётся в силе, менять его в одностороннем порядке нельзя.
 * Значение при этом ровно одно, и живёт оно в общем пакете — рядом с самим
 * перечислением, чьи ключи обязано покрывать.
 *
 * ⚠️ КЛЮЧ `IN_REVIEW` ПЕРЕИМЕНОВАН В `ON_REVIEW` (Ф3 · В3a, 09.08.2026).
 * Перечисление БД (миграция 046) и его зеркало `WorkStatus` в
 * `packages/shared/src/work.ts` называют состояние `ON_REVIEW`; здесь ключ был
 * написан `IN_REVIEW`. Расхождение тихое и дорогое: компонент, индексирующий
 * словарь СЕРВЕРНЫМ значением (`workStatusRu[work.status]`), получал бы
 * `undefined` — пустое место на экране ровно в том состоянии, вокруг которого
 * стоит решение заказчика 4 («исполнителю доступно „на проверке"»).
 * Потребителей у прежнего ключа не было ни одного (проверено грепом по
 * `apps/web/src`), поэтому правка не ломает ничего и делается сейчас, а не
 * после того, как на неё наткнутся.
 */
export const workStatusRu = WORK_STATUS_LABEL;

// ⚠️ `workStageRu` / `workStageFullRu` СНЯТЫ 11.08.2026 по решению заказчика
// (DECISIONS.md, Р-1): стадий ПД/РД в системе больше нет — ни колонки, ни
// перечисления, ни фильтра на экране. ПД и РД теперь два разных ОБЪЕКТА.
// Заводить подписи обратно «на всякий случай» не надо: подпись без носителя
// — приглашение вернуть поле мимо решения заказчика.

/**
 * Вычисляемые признаки поверх состояния. Ни один из них **не хранится**
 * в базе — оба выводятся из `dueDate` относительно серверного `today()`.
 *
 * - `urgent` — `dueDate <= today + 1д` (решение заказчика 21: хранимый
 *   приоритет убран, срочность выводится из срока);
 * - `overdue` — `dueDate < today` и работа не в состоянии «Готово».
 *   ТЗ §5.3-c прямо: «просрочка вычисляется по сроку и отображается
 *   признаком, отдельным статусом не является».
 *
 * Признак — не состояние: он показывается **вместе** с любым из четырёх
 * состояний, а не вместо него.
 */
export const workFlagRu = {
  urgent: 'Срочно',
  overdue: 'Просрочено',
} as const;

/**
 * Подписи пары дат. У работы **две** даты, обе обязательны
 * (решение заказчика 2; начало по умолчанию — сегодня).
 *
 * Договорного срока у работы нет: он живёт на уровне объекта и приходит
 * из договора (решение 13, умолчание У-1 протокола В0.5). Отсюда
 * `contractDeadline` подписан как срок **объекта**, а не работы — чтобы
 * подпись нельзя было переиспользовать на карточке работы по ошибке.
 */
export const workDatesRu = {
  /** `Work.startDate`. */
  start: 'Начало',
  /** `Work.dueDate` — собственный срок работы. */
  due: 'Срок',
  /** Пара целиком — заголовок группы полей в форме. */
  period: 'Период',
  /** `Project` — договорной срок объекта, справочно рядом со сроками работ. */
  contractDeadline: 'Договорной срок объекта',
  /** `Project` — плановый срок объекта (ТЗ §5.3-a). */
  plannedDeadline: 'Плановый срок объекта',
} as const;

/**
 * Лексика примитивов `components/work/` — Блок 2 · Ф3 · В3a.
 *
 * Заведена ЗДЕСЬ, а не в компонентах, по общему правилу словаря и по одной
 * дополнительной причине: этими примитивами пользуются ТРИ волны (В3, В4, В5).
 * Литерал, написанный в компоненте, разошёлся бы с литералом соседней волны —
 * и «Срочно» на карточке объекта соседствовало бы со «Срочная» на Ганте.
 */
export const workPrimitivesRu = {
  /**
   * Подсказка под парой дат. Функции, а не строки: число склоняется
   * (`pluralRu`), и склонение, оставленное вызывающему, разъедется по экранам.
   *
   * ⚠️ ТРИ СОСТОЯНИЯ, А НЕ ДВА. «Сегодня» — отдельная ветка, потому что «через
   * 0 дн» по-русски не говорят, а «просрочено на 0 дн» неверно фактически:
   * день срока ещё не истёк.
   */
  dueHint: {
    overdue: (days: number) => `Просрочено на ${days} ${dayWord(days)}`,
    today: 'Срок сегодня',
    ahead: (days: number) => `Через ${days} ${dayWord(days)}`,
  },
  /**
   * Шкала состояний.
   *
   * ═══ ⚠️ ТЕКСТ ОТКАЗА РАЗЛИЧАЕТ ВИД, И ЭТО НЕ ОФОРМЛЕНИЕ (Ф4 · В6-б) ══════
   *
   * «…или РО отдела исполнителя» НА ЗАДАЧЕ — ложь буквальная: объект тут ни
   * при чём, право держит постановщик (Р-18 + Р-20 + Р-23). Прочитав общий
   * текст, человек пойдёт искать РО объекта и не найдёт: у служебного объекта
   * строк `ProjectManager` нет вовсе.
   *
   * ⚠️ ЧЕТЫРЕ СТРОКИ ОТКАЗА СОВПАДАЮТ С СЕРВЕРНЫМИ СЛОВО В СЛОВО — `WORK_DENIAL`
   * в `apps/api/src/work/work.service.ts`, ключи `finalize` и `reopen`.
   * Расхождение уже случалось однажды (клиент говорил «Вернуть завершённую
   * работу…», сервер — «…работу В РАБОТУ…»), и нажавший в обход формы получал
   * ДРУГУЮ формулировку того же отказа, то есть решал, что причин две.
   *
   * ⚠️ ДВЕ СТРОКИ `PROJECT_WORK` ПЕРЕПИСАНЫ 24.08.2026 ВМЕСТЕ С СЕРВЕРОМ
   * (решение заказчика **Р-82**). 🚫 Стояло: ~~«…может только тот, кто **ведёт
   * объект**»~~. После сужения права до отдела исполнителя этот текст стал
   * ложью ровно для того, кто объект ВЕДЁТ, а слово «ведущий» решение
   * запрещает отдельной врезкой. Полный разбор — над `WORK_DENIAL` на сервере;
   * здесь он не пересказывается, чтобы два текста об одном решении не
   * разошлись.
   *
   * ═══ 🚫 ПАРА `finalizeElsewhere` / `reopenElsewhere` СНЯТА (Ф4 · В6-б) ═══
   *
   * Она отвечала на вопрос ГДЕ ЭТО СДЕЛАТЬ и была верна ровно пока экран НЕ МОГ
   * узнать право: `GET /works/mine` признака не отдавал, `canFinalize` на «Моей
   * работе» подставлялся `false` в безопасную сторону — и текст ПРО ПРАВИЛО
   * показывался в том числе тому, кто объект ВЕДЁТ.
   *
   * ⚠️ ОСНОВАНИЕ ИСЧЕЗЛО ВМЕСТЕ С ПРИЧИНОЙ, А НЕ ЗАБЫЛОСЬ: подволна В6-а
   * положила в `WorkResponse` поле `canFinalize`, посчитанное сервером по той же
   * строке и тем же гейтом. Состояния «экран права не знает» не осталось ни на
   * одном экране — значит второй пары текстов быть не должно: она сообщала бы
   * «сделайте это в другом месте» тому, кто вправе сделать это здесь.
   *
   * ⚠️ ЧТО ИЗ ТОЙ НАХОДКИ ОСТАЁТСЯ ВЕРНЫМ: подпись обязана быть верной ЛЮБОМУ
   * читателю. Теперь это достигается не обходным текстом про место, а тем, что
   * подпись не показывается вовсе тому, у кого право есть.
   */
  statusScale: {
    label: 'Состояние работы',
    /*
     * ⚠️ ДВЕ СТРОКИ `PROJECT_WORK` НИЖЕ ЗЕРКАЛЯТ СЕРВЕР СЛОВО В СЛОВО —
     * `apps/api/src/work/work.service.ts`, `WORK_DENIAL.finalize` и
     * `WORK_DENIAL.reopen`. Правка там без правки здесь даёт две формулировки
     * одного отказа, и человек решает, что причин две; так уже случалось.
     *
     * 🚫 Обе переписаны 25.08.2026 по решению заказчика **Р-86 «ГИП управляет
     * объектом, а не работами отдела — административные роли выходят из
     * проектных работ целиком»**. Стояло: ~~«могут **администратор, ГИП
     * объекта** или РО отдела исполнителя»~~. Права у первых двух нет вовсе,
     * и перечень, называющий их держателями, отправлял бы отказанного за
     * помощью к тому, кто откажет тоже.
     */
    /**
     * Подсказка на ЗАПРЕЩЁННОЙ ступени «готово». Формулируется через того, кто
     * вправе, а не через отказ: «нельзя» без адресата заставляет пользователя
     * писать в поддержку.
     */
    finalizeDenied: {
      [WorkKindEnum.PROJECT_WORK]:
        'Завершить работу может только РО отдела исполнителя, вошедший в объект',
      [WorkKindEnum.TASK]: 'Завершить задачу может только тот, кто её поставил',
    },
    /**
     * Вторая половина ДВУСТОРОННЕГО потолка (решение заказчика 08.08.2026:
     * «Завершённую работу возвращает в работу только руководитель»; у задачи —
     * Р-20, возврат из «готово» тоже у постановщика). Без неё исполнитель
     * распечатывал бы закрытое в одно действие.
     */
    reopenDenied: {
      [WorkKindEnum.PROJECT_WORK]:
        'Вернуть завершённую работу в работу может только РО отдела исполнителя, вошедший в объект',
      [WorkKindEnum.TASK]: 'Вернуть завершённую задачу в работу может только тот, кто её поставил',
    },
    /** Снятая строка не меняет состояние НИКЕМ — включая постановщика задачи. */
    archived: {
      [WorkKindEnum.PROJECT_WORK]: 'Работа снята — состояние не меняется',
      [WorkKindEnum.TASK]: 'Задача снята — состояние не меняется',
    },
  },
  /** Одиночный выбор исполнителя. */
  assignee: {
    label: 'Исполнитель',
    placeholder: 'Выберите сотрудника',
    searchPlaceholder: 'Фамилия или имя',
    empty: 'Подходящих сотрудников нет',
    /**
     * Почему список короче, чем ожидал пользователь. Подсказка обязательна:
     * правила делегирования (`assert-assignees.ts`) отсекают кандидатов молча,
     * и без объяснения экран выглядит сломанным, а не ограниченным.
     */
    scopeHintOwnDept: 'Показаны сотрудники вашего отдела',
    scopeHintEligible: 'Показаны активные сотрудники с указанным отделом',
    /** Актору назначение запрещено вовсе — это НЕ «людей нет». */
    scopeHintNoRight: 'Назначать исполнителей на этом объекте вам нельзя',
    /** Кандидаты есть, но все отсеяны правилами — тоже не «людей нет». */
    scopeHintAllFiltered: 'Все сотрудники отсеяны правилами назначения',
    loading: 'Загружаем сотрудников…',
    /**
     * Режим `hierarchy` — ЗАДАЧА (Ф4 · В6-б, Р-16).
     *
     * ⚠️ ОБЛАСТЬ У КАЖДОГО УРОВНЯ СВОЯ, И ОБЩИЙ ТЕКСТ ЧИТАЛСЯ БЫ КАК ПОЛОМКА.
     * У рядового в списке ровно один человек — он сам; подпись «показаны
     * активные сотрудники с указанным отделом» под списком из одной строки
     * отправляет чинить кадры, а дело в правиле.
     */
    hierarchy: {
      employee: 'Задачу вы можете завести только себе',
      manager: 'Себе и сотрудникам вашего отдела',
      admin: 'Только руководителям отделов — себе задачу администратор не заводит',
      noRight: 'Задачи вам заводить нельзя',
    },
  },
  /** Счётчик параллельных работ (F1.3). */
  parallel: {
    label: 'Занятость в этом периоде',
    /** ⚠️ ЧАСОВ НЕТ — маршрут их не отдаёт, вердикт «перегруз» выносить не из чего. */
    total: (n: number) => `${n} ${workWord(n)} в этом периоде`,
    dueSoon: (n: number) => `из них срочных: ${n}`,
    absenceDays: (n: number) => `отсутствий в периоде: ${n} ${dayWord(n)}`,
    none: 'В этом периоде работ нет',
    /** Полоса без содержания: проект строки актору невидим. */
    masked: 'Другой проект',
  },
} as const;

/** «день / дня / дней» — одно место на весь модуль. */
function dayWord(n: number): string {
  return pluralRu(n, ['день', 'дня', 'дней']);
}

/** «работа / работы / работ» — одно место на весь модуль. */
function workWord(n: number): string {
  return pluralRu(n, ['работа', 'работы', 'работ']);
}

/**
 * Служебный объект «Внутренние работы» (решение заказчика 29.07.2026,
 * протокол В0.5 пункт 6; отменено 06.08.2026, ВОЗВРАЩЕНО решением Р-9 от
 * 11.08.2026).
 *
 * ⚠️ ПРЕЖНИЙ ДОКСТРИНГ ОПИСЫВАЛ ЗАМЫСЕЛ КАК СДЕЛАННОЕ, И ВСЕ ТРИ ЕГО
 * УТВЕРЖДЕНИЯ БЫЛИ НЕВЕРНЫ. Он говорил: «реализуется объектом с признаком
 * `Project.isInternal`, разделы которого — виды внутренней работы; объект
 * скрыт фильтром по умолчанию в списке объектов, на графике загруженности
 * и в отчётах по объектам». Как на самом деле (Ф4 · В-ВР):
 *
 *   - колонки `Project.isInternal` НЕТ и не будет: признак служебности —
 *     существующий `code = 'OTHER'` (`MISC_PROJECT_CODE`). Гарантия
 *     «служебный один» уже куплена частичным уникальным индексом
 *     `project_code_other_unique` (миграция 031), и второй признак завёл
 *     бы две несогласуемые истины;
 *   - объект скрыт ТОЛЬКО в основном списке объектов (`GET /projects` без
 *     `scope`) и в панели «активные объекты» дашборда;
 *   - ⚠️ НА ГРАФИКЕ ЗАГРУЖЕННОСТИ И ТЕПЛОВОЙ КАРТЕ ЕГО ПРЯТАТЬ НЕЛЬЗЯ, и
 *     это не недоделка, а правило: часы на внутренней работе — это часы
 *     сотрудника, и на его загруженности они обязаны быть видны. Прятать
 *     их значило бы объявить внутреннюю работу невыполненной. Там же
 *     остаётся и окно отчёта о часах — это ЕДИНСТВЕННЫЙ путь списать
 *     внутренние часы;
 *   - «отчётов по объектам» в репозитории пока нет вовсе (Ф7).
 *
 * `kinds` — стартовый состав видов; список редактируемый, поэтому
 * подписи здесь нужны только для мест, где вид упоминается в статике
 * (подсказка в форме, пустое состояние). Живые названия приходят из API:
 * виды внутренней работы — это РАЗДЕЛЫ служебного объекта, а разделы
 * берутся из справочника разделов (решение Р-10). Отдельного справочника
 * видов не существует.
 *
 * ⚠️ 🚫 Прежде справочник назывался здесь адресом экрана —
 * «`/supervisor/catalog`», — и это читалось как «наполняет его супервайзер».
 * После **Р-90** стартовый состав кладёт в справочник ЗАГРУЗЧИК
 * (`ProjectsBootstrapService.ensureInternalWorkKinds`); экран супервайзера
 * остаётся местом, где состав ПРАВЯТ и ДОПОЛНЯЮТ (положение 3), а не местом,
 * откуда он берётся впервые.
 *
 * ⚠️ Потребитель у блока ровно один — подпись переключателя «показать
 * служебный объект» на `/projects` (`projectName`). Остальные ключи
 * (`pickerHint`, `sectionLabel`, `kinds`) ждут форму задачи, её пишет
 * волна В6.
 *
 * 🚫 **ПРЕЖНИЙ ДОВОД СОХРАНЕНИЯ БЛОКА ЦЕЛИКОМ ОТМЕНЁН РЕШЕНИЕМ Р-90
 * (25.08.2026):** «состав `kinds` — предмет ПОРУЧЕНИЯ СУПЕРВАЙЗЕРУ (завести
 * группу „Внутренние работы“ с этими пунктами через `/supervisor/catalog`)».
 * Поручения больше нет: группу с пунктами заводит ЗАГРУЗЧИК
 * (`apps/api/src/projects/projects-bootstrap.service.ts`), потому что
 * служебный объект «даже в интерфейсе не отображается» — дословно владелец,
 * — и никакой ГИП настраивать его справочник не пойдёт.
 *
 * ⚠️ **ВЫВОД «ЧИТАТЬСЯ ИЗ ОДНОГО МЕСТА» ПРИ ЭТОМ УСИЛЕН, А НЕ СНЯТ:** место
 * стало общим пакетом (`INTERNAL_WORK_KINDS` в `@aris/shared`), потому что
 * читателей теперь двое — фронт и загрузчик.
 */
export const internalWorkRu = {
  /** Наименование служебного объекта. Источник один — общий пакет:
   *  фронт не вправе знать это имя вторым литералом (см. `projects.ts`,
   *  `MISC_PROJECT_NAME`). */
  projectName: MISC_PROJECT_NAME,
  /** Подпись пункта в пикере объекта (В6, форма задачи). */
  pickerHint: 'непроектная работа',
  /**
   * Подпись поля «раздел» у служебного объекта — это вид работы, не раздел.
   *
   * ⚠️ ЗНАЧЕНИЕ ПЕРЕЕХАЛО В ОБЩИЙ ПАКЕТ (Ф5 · В9-b, **Р-70, граница 6**), здесь
   * остался ПЕРЕЭКСПОРТ — та же форма, что у `kinds` ниже и по той же причине:
   * подпись читает теперь и `apps/api` (пикер раздела в боте). Значение НЕ
   * менялось ни одной буквой.
   */
  sectionLabel: SECTION_FIELD_LABEL.internal,
  /**
   * Стартовый состав видов внутренней работы.
   *
   * ⚠️ ЗНАЧЕНИЕ ПЕРЕЕХАЛО В ОБЩИЙ ПАКЕТ (Ф5 · В9-d, решение **Р-90**), здесь
   * остался ПЕРЕЭКСПОРТ. Причина не в порядке, а в том, что состав читает
   * теперь и `apps/api` — его берёт ЗАГРУЗЧИК
   * (`ProjectsBootstrapService.ensureInternalWorkKinds`), а второй литерал в
   * втором месте отклонён прецедентом **Р-70, граница 6**. Полный разбор
   * («Разное», а не «Прочее»; состав открытый, а не эталон) — у
   * `INTERNAL_WORK_KINDS` в `@aris/shared`, `projects.ts`. Значение отсюда
   * НЕ МЕНЯЛОСЬ ни одной строкой: те же четыре пункта в том же порядке.
   *
   * 🚫 **ОТМЕНЕНО РЕШЕНИЕМ Р-90 (25.08.2026):** «состав отсюда супервайзер
   * вносит руками через `/supervisor/catalog`». Приведено, а не стёрто, —
   * иначе следующий прочтёт умолчание как поручение и построит отменённое.
   * ⚠️ **ОТМЕНЁН СПОСОБ, А НЕ ПРАВО:** группу с пунктами заводит загрузчик,
   * но справочник супервайзеру НЕ закрывается — он вправе **дополнять**
   * состав (Р-90, положение 3), и загрузчик добавленное не сносит.
   *
   * `SECTION_MISC_LABEL` этим НЕ трогается: лейн без раздела — другой
   * предмет, и переименовывать его здесь нечего.
   */
  kinds: INTERNAL_WORK_KINDS,
} as const;

/**
 * Названия пунктов меню (решение заказчика 10 — «Моя работа» и «Задачи»
 * оставляем как есть).
 *
 * `workload` переименован «Загрузка» → «Загруженность» (0b-T3): ТЗ ред. 2
 * называет так и §4.4, и экран №3 в §7.1. Буквальная формулировка
 * ПРИЁМКА-8 («в разделе „Загрузка“») расходится — снимается протоколом
 * В0.5, пункт 7.
 *
 * Пункт `tasks` живёт здесь, а не в компоненте, по общему правилу словаря:
 * подпись меняется одним диффом. Прежде у него была вторая, более сильная
 * причина — «Задачи» было единственным легальным местом запрещённой лексемы
 * под `apps/web/src`; после разворота правила 07.08.2026 это уже не так, и
 * причина осталась одна.
 */
export const workNavRu = {
  /** Личный экран сотрудника (В5). */
  myWork: 'Моя работа',
  /**
   * Экран `/tasks` — «**Поручения**» (Ф4 · В-ПР-2б, Р-29).
   *
   * ⚠️ ПЕРЕИМЕНОВАН ЭКРАН, А НЕ МАРШРУТ. Путь `/tasks` остаётся как был: смена
   * пути ломает сохранённые ссылки и закладки и решением не названа.
   *
   * ⚠️ КЛЮЧ ЧИТАЮТ ДВОЕ — пункт меню (`layouts/root-layout.tsx`) и ЗАГОЛОВОК
   * САМОГО ЭКРАНА (`pages/tasks/tasks.page.tsx`). Прежде заголовок жил
   * отдельным литералом в `taskScreenRu.heading`, и после Р-29 меню и экран
   * назывались бы по-разному — молча, потому что правку сделали бы в одном
   * месте из двух. Поэтому `heading` снят, а не переписан.
   *
   * 🚫 Прежняя пометка «живого маршрута ещё нет» СНЯТА ФАКТОМ: маршрут пришёл
   * волной В6.
   *
   * ═══ ⚠️ ЕДИНСТВЕННОЕ ПОСЛАБЛЕНИЕ ЛЕКСИЧЕСКОГО ГЕЙТА — И ОНО ТОЧЕЧНОЕ ═════
   *
   * `aris/no-legacy-lexeme` запрещает «поручен…» с 07.08.2026. ⚠️ ОСНОВАНИЕ
   * ПРАВИЛА НЕ ОТМЕНЕНО И ДЕЙСТВУЕТ ЦЕЛИКОМ: второй ВИД РАБОТЫ называется
   * «задачей» (`WorkKind.TASK`), и подпись `TASK: 'Поручение'` в этом же файле
   * остаётся ровно тем разъездом лексики, ради которого гейт заведён.
   *
   * **Что изменилось — переименован ЭКРАН, а не вид работы** (Р-29 от
   * 17.08.2026, решение заказчика). Экран отвечает на вопрос «чего я жду от
   * ДРУГИХ», и «Задачи» для него стало ложным именем: задачи актора живут на
   * «Моей работе». Различить эти два случая регулярным выражением нельзя —
   * лексема одна, — поэтому послабление стоит ОДНОЙ СТРОКОЙ здесь, а не
   * расширением `LEXEME_ALLOWLIST`: широкий глоб пропустил бы и подпись вида
   * работы, то есть снял бы гейт там, где он и нужен.
   *
   * ⚠️ ⚠️ ЭТО ЕДИНСТВЕННОЕ МЕСТО ВО ВСЁМ ДЕРЕВЕ, ГДЕ ЛЕКСЕМА ЗАКОННА. Все
   * прочие места волны переписаны так, чтобы обойтись без неё, а тесты берут
   * подпись ИМПОРТОМ отсюда — тем же, что и компонент (прямо рекомендовано
   * докблоком `LEXEME_ALLOWLIST`). Строковый литерал с этим словом в любом
   * другом файле — дефект, и гейт обязан его поймать.
   *
   * ⚠️ ПОСЛАБЛЕНИЕ ВНЕСЕНО ИСПОЛНИТЕЛЕМ ВОЛНЫ И ТРЕБУЕТ РЕВЮ: докблок правила
   * прямо говорит, что послабления вносятся «после ревью, а не для того, чтобы
   * позеленело». Названо отдельной строкой отчёта волны.
   */
  // eslint-disable-next-line aris/no-legacy-lexeme -- Р-29: переименован ЭКРАН, а не WorkKind.TASK; разбор во врезке выше
  tasks: 'Поручения',
  /** Экран загруженности (ТЗ §4.4, §7.1 строка 3). */
  workload: 'Загруженность',
} as const;

/**
 * Терминология верхнего уровня — три уровня модели.
 *
 * **Осознанное расхождение, не опечатка:** ТЗ называет сущность
 * «объект», действующий интерфейс и модель БД — «Проект» / `Project`.
 * Ф0 ничего не переименовывает; расхождение зафиксировано в
 * `.planning/block-2-object-tracker/GLOSSARY.md`, вопрос заказчику ушёл
 * сопроводительным письмом к протоколу В0.5. До ответа UI говорит
 * «Проекты» — менять подпись здесь в одностороннем порядке нельзя.
 */
export const workHierarchyRu = {
  /** ТЗ — «объект»; UI и модель — «проект» / `Project`. См. GLOSSARY.md. */
  project: 'Проект',
  /** `Section` — раздел проектирования (ТЗ §5.1, каталог §5.2). */
  section: 'Раздел',
  /** `Work` — работа; уровня «этап» между ними нет (В0.5, п. 3). */
  work: 'Работа',
} as const;

/**
 * Лексика графика занятости — Блок 2 · Ф3 · В4.
 *
 * ⚠️ «Другой проект» — ПОДПИСЬ, КОТОРУЮ ДОРИСОВЫВАЕТ ФРОНТ, и это не
 * оформление, а часть требования. Сервер у маскированной полосы вычищает ВСЕ
 * текстовые поля, включая название (`workload-gantt.service.ts`), поэтому без
 * этой подписи полоса нема: пользователь видит занятость без единого слова и
 * читает её как ошибку загрузки.
 *
 * И то же требование действует В ОБЕ СТОРОНЫ: у полосы, у которой проекта
 * ПРОСТО НЕТ (задача), подпись запрещена — она соврала бы о сокрытии там, где
 * прятать нечего. Различает эти два состояния ТОЛЬКО поле `masked`:
 * `projectId` у обоих `null`.
 */
export const ganttRu = {
  /** Подпись маскированной полосы. Дорисовывается фронтом — см. врезку. */
  maskedProject: 'Другой проект',
  /**
   * Пояснение к ней в тултипе — почему полоса пуста.
   *
   * ⚠️ ЛЕКСИКА СВЕДЕНА К «ПРОЕКТУ» (П-8). График говорил «объект», а панель
   * фильтров ТОЙ ЖЕ страницы — «Проект»: два слова про одну сущность на одном
   * экране, и пользователь ищет между ними разницу, которой нет. ТЗ называет
   * сущность «объект», интерфейс и модель БД — «Проект» (`workHierarchyRu`,
   * расхождение зафиксировано в GLOSSARY.md и закрыто вопросом заказчику). До
   * ответа весь экран говорит ОДНИМ словом — тем, что уже на кнопках.
   */
  maskedHint: 'Проект недоступен вам для просмотра',
  /** Полоса задачи без проекта: у неё есть название, и оно и есть подпись. */
  noProject: 'Без проекта',
  today: 'Сегодня',
  /**
   * Переключатель шага оси.
   *
   * ⚠️ ШАГ ОСИ — НЕ ПЕРИОД ОКНА. Панель фильтров той же страницы несёт
   * «Неделя / Месяц» (`WorkloadPeriod`) — это ГРАНИЦЫ показываемого отрезка.
   * Здесь же — плотность столбцов ВНУТРИ выбранных границ: год можно смотреть
   * недельным шагом, месяц — дневным.
   *
   * ⚠️ СЛОВА РАЗВЕДЕНЫ НАМЕРЕННО, И ОДНОЙ ПОДПИСИ ГРУППЫ БЫЛО МАЛО. Прежде
   * кнопки звались «День / Неделя», и на экране оказывалось ДВЕ кнопки
   * «Неделя» с противоположным состоянием: диктор читал их подряд, а
   * пользователь в режиме «Месяц» жал «Неделю» у графика, ожидая сменить
   * период, и получал тот же месяц, только сплюснутый. Здесь предлог: «по
   * дням» и «по неделям» отвечают на вопрос «как показывать», а не «за
   * какой срок», и совпадающего слова на экране больше нет.
   */
  scaleGroup: 'Шаг оси',
  scaleDay: 'По дням',
  scaleWeek: 'По неделям',
  conflict: 'Работа приходится на отсутствие',
  clippedStart: 'Начало раньше периода',
  clippedEnd: 'Срок позже периода',
  /**
   * Р-50 — подсказка обязана назвать ОБЕ даты.
   *
   * ⚠️ БЕЗ ЭТОЙ СТРОКИ ПОДСКАЗКА ПРОТИВОРЕЧИТ НАРИСОВАННОМУ КРАЮ. У
   * завершённой работы полоса кончается днём ЗАКРЫТИЯ, а диапазон в подсказке
   * печатается по СРОКУ: работа со сроком 10-го, закрытая 18-го, рисуется до
   * 18-го и подписывается «… — 10 августа». Утверждение, пережившее правку,
   * которая сделала его ложным, — и оно бы пережило, потому что подсказка
   * формально верна: срок действительно 10-е.
   */
  closedOn: 'Закрыта',
  emptyRow: 'Работ в периоде нет',
  empty: 'В выбранном периоде показывать нечего',
  /** Признак завершённой работы — ВТОРОЙ канал помимо начертания (Б-3). */
  done: 'Завершено',
  weekend: 'Выходной',
  /**
   * Полос больше, чем помещается под-дорожек: остаток свёрнут (Д-5).
   *
   * ⚠️ ПОДПИСЬ НАЗЫВАЕТ ДЕЙСТВИЕ, А НЕ ФАКТ — Ф4 · В-ПР, Р-32 в редакции
   * поправки 17.08.2026 «подпись кнопки называет ДЕЙСТВИЕ». Прежняя редакция
   * печатала «+16 работ не показано», и владелец, глядя прямо на эту надпись,
   * способа увидеть остальные НЕ НАШЁЛ: констатация показывает проблему и
   * молчит о выходе. Кнопка с той же констатацией на лице оставила бы исходный
   * дефект ровно там, где его чинят, — поэтому текста «+N» здесь больше нет.
   *
   * ⚠️ ЧИСЛО ОСТАЁТСЯ. Убрана констатация, а не счёт: сколько именно скрыто —
   * это и есть содержание предупреждения, и без числа кнопка перестала бы
   * говорить о потере вовсе.
   *
   * ⚠️ СКЛОНЕНИЕ НЕ ПОТЕРЯНО ПРИ ПРАВКЕ ПОДПИСИ. У прежней редакции оно уже
   * чинилось (причастие было константой среднего рода, и «+1 работа не
   * показано» читалось на стенде). Здесь падеж ВИНИТЕЛЬНЫЙ — «показать ещё 1
   * работУ», — то есть формы другие, чем у именительного, и списывать их у
   * соседнего словаря нельзя.
   */
  laneExpand: (n: number) => `Показать ещё ${n} ${pluralRu(n, ['работу', 'работы', 'работ'])}`,
  /**
   * Подпись той же кнопки в РАЗВЁРНУТОМ состоянии.
   *
   * ⚠️ ПОДПИСЬ ПЕРЕКЛЮЧАЕТСЯ ВМЕСТЕ С `aria-expanded`, И ЭТО НЕ УДВОЕНИЕ ОДНОГО
   * ПРИЗНАКА. Постоянное «показать ещё N» при верно переключающемся
   * `aria-expanded` даёт диктору «Показать ещё 3 работы, развёрнуто» — кнопка
   * предлагает уже сделанное. Доступное имя обязано быть верным в ОБОИХ
   * состояниях, а не в одном из двух.
   */
  laneCollapse: 'Свернуть работы',
  laneOverflowHint: 'Сузьте период или отфильтруйте по проекту, чтобы увидеть остальные',

  // ─── Признак отсечённого фильтром ────────────────────────────────────────
  //
  // ⚠️ ПЕЧАТАЮТСЯ ОБА ЧИСЛА. Ради этого серверный счётчик и считает ДВА:
  // фильтр по проекту сужает не только полосы, но и САМУ КОГОРТУ — человек
  // исчезает СТРОКОЙ ЦЕЛИКОМ. Руководитель на отфильтрованном графике видит
  // меньше ЛЮДЕЙ, чем есть, и «скрыто 3 работы» при пяти исчезнувших
  // сотрудниках — сообщение, которое УСПОКАИВАЕТ вместо того, чтобы
  // предупредить.
  //
  // Части раздельные, потому что нулевое печатать нельзя: «0 сотрудников» —
  // ровно то состояние, которое условие показа запрещает (Б-4). Склонения
  // обязательны: «1 работ» неверно.
  hiddenPrefix: 'Скрыто фильтром:',
  hiddenBars: (n: number) => `${n} ${pluralRu(n, ['работа', 'работы', 'работ'])}`,
  hiddenRows: (n: number) => `${n} ${pluralRu(n, ['сотрудник', 'сотрудника', 'сотрудников'])}`,
  /**
   * Область счёта у администратора БЕЗ выбранного отдела.
   *
   * ⚠️ ЭТО УТОЧНЕНИЕ, А НЕ ЗАМЕНА ЧИСЛА, и разница стоила блокера. Прежняя
   * редакция при этом признаке число скрытых строк ПОДАВЛЯЛА — из опасения,
   * что «5 сотрудников» прочтётся как «от вас скрыли пол-компании». Вышло
   * хуже: у администратора, то есть у самого частого зрителя отфильтрованного
   * графика, число не выводилось НИ ОДНИМ СЛОВОМ и жило только в разметке.
   * Опасение снимается ПОЯСНЕНИЕМ ОБЛАСТИ, а не молчанием.
   */
  hiddenWholeOrgScope: 'по всей организации — отдел не выбран',
  /**
   * ⚠️ ПОЯСНЕНИЕ ОБЯЗАНО НАЗЫВАТЬ ОБА ОСНОВАНИЯ — 19.08.2026, ПРАВКА ПО ФАКТУ
   * СО СТЕНДА.
   *
   * Прежняя редакция: «…и сотрудников, НЕ УЧАСТВУЮЩИХ в выбранном». Она была
   * верна ровно до того дня, когда когорта научилась ИСПОЛНИТЕЛЯМ работ
   * (`workload-cohort.ts`, ветка `scope.projectId`: участник ИЛИ исполнитель
   * живой работы объекта). После этого текст стал спорить с экраном, и хуже
   * всего — на служебном объекте «Внутренние работы»: участников там нет ПО
   * УСТРОЙСТВУ (Р-22), человек видит СЕБЯ в списке и читает рядом объяснение,
   * по которому его быть не должно.
   *
   * ⚠️ ЦЕНА НЕ КОСМЕТИЧЕСКАЯ, И ОНА НЕ В КРАСОТЕ ФОРМУЛИРОВКИ. Не найдя
   * коллегу, человек объяснит его отсутствие НЕВЕРНОЙ причиной («он не
   * участник») и разбираться не станет. Ложное объяснение дороже отсутствующего.
   *
   * ⚠️ ЧТО ЭТУ СТРОКУ СТЕРЕЖЁТ И ЧЕГО СТОРОЖ НЕ ВИДИТ. Сторож —
   * `workload-gantt-view.test.tsx`, пара «пояснение называет оба основания» +
   * «прежней формулировки на экране нет». Он смотрит НА ТЕКСТ, а не на
   * предикат: верни кто-нибудь когорту к одному участию — текст снова станет
   * правдой, и сторож промолчит. Ту сторону держат серверные спеки
   * (`workload-cohort-assignee-e2e.spec.ts`), и это разделение намеренное.
   */
  hiddenHint:
    'Фильтр по проекту убирает и работы других проектов, и сотрудников, ' +
    'у которых в выбранном проекте нет ни работ, ни участия. ' +
    'Снимите фильтр, чтобы увидеть всё.',
} as const;

/**
 * Личный экран «Моя работа» — Блок 2 · Ф4 · В-МР (Р-28).
 *
 * ⚠️ ДВА УРОВНЯ, И ОНИ НЕ РАВНОПРАВНЫ. Первый — ВИД работы (вкладки), второй —
 * СОСТОЯНИЕ внутри выбранного вида. Порядок несущий: вынести «Завершённые»
 * третьей вкладкой РЯДОМ с видами значило бы получить там ровно ту кашу, ради
 * устранения которой Р-28 и принято, — задачи и проектные работы вперемешку.
 *
 * ⚠️ «Проектные работы» — СВОЯ подпись, а не `workKindPluralRu.PROJECT_WORK`
 * («Работы»). Рядом с вкладкой «Задачи» одно слово «Работы» читается как «всё
 * остальное», хотя это конкретный вид; Р-28 называет обе вкладки дословно, и
 * словарь повторяет решение буквой, а не пересказом.
 */
/**
 * Подписи вкладок и состояний — ОТДЕЛЬНЫМИ константами, а не только полями
 * `myWorkRu`.
 *
 * ⚠️ ЭТО НЕ ОФОРМЛЕНИЕ: `liveCell` ниже собирает объявление ИЗ ЭТИХ ЖЕ подписей,
 * а поле объекта не может ссылаться на соседнее поле того же литерала — TS
 * отказывается выводить тип, ссылающийся сам на себя. Второй набор подписей
 * рядом разошёлся бы с первым молча: на экране «Завершённые», в ухе «Готовые».
 */
const myWorkKindTabRu = {
  TASK: 'Задачи',
  PROJECT_WORK: 'Проектные работы',
} as const;

const myWorkStateTabRu = {
  active: 'В работе',
  done: 'Завершённые',
  archived: 'Снятые',
} as const;

/**
 * Пустая ЯЧЕЙКА — не то же, что пустой экран.
 *
 * ⚠️ ТЕКСТ НАЗЫВАЕТ ЯЧЕЙКУ, А НЕ ПУСТОТУ ВООБЩЕ. Общее «Пока ничего не
 * назначено» во вкладке «Задачи» — ложь человеку, у которого двенадцать
 * проектных работ на соседней вкладке: он прочитает его как поломку экрана
 * или как «работы кончились». Пустота законна, и сказать надо, ЧЕГО именно
 * нет и где искать остальное.
 *
 * ⚠️ ЭТОТ ЖЕ ТЕКСТ ЗВУЧИТ В ЖИВОЙ ОБЛАСТИ (`liveCell`) — один источник на глаз
 * и на слух. Пока источников было два, зрячий слышал… то есть видел одно, а
 * незрячий слышал другое, и лечение первого не доходило до второго.
 */
const myWorkEmptyCellRu = {
  TASK: {
    active: 'Задач в работе нет',
    done: 'Завершённых задач нет',
    archived: 'Снятых задач нет',
  },
  PROJECT_WORK: {
    active: 'Проектных работ в работе нет',
    done: 'Завершённых проектных работ нет',
    archived: 'Снятых проектных работ нет',
  },
} as const;

export const myWorkRu = {
  /** Подзаголовок экрана. Заголовок живёт в `workNavRu.myWork`. */
  subtitle: 'Работы и задачи, которые вы делаете. Состояние двигается прямо здесь.',
  /** Первый уровень — вид работы. Ключи совпадают с `Work.kind`. */
  kindGroupLabel: 'Вид работы',
  kindTab: myWorkKindTabRu,
  /** Второй уровень — состояние внутри вида. */
  stateGroupLabel: 'Состояние',
  stateTab: myWorkStateTabRu,
  emptyCell: myWorkEmptyCellRu,
  /** Подсказка под пустой ячейкой: числа соседних ячеек стоят на переключателях. */
  emptyCellHint: 'Числа на вкладках и переключателях показывают, где лежит остальное.',
  /** Совсем пустая выдача — ни одной строки ни в одной ячейке. */
  emptyAll: 'Пока ничего не назначено',
  emptyAllHint: 'Здесь появятся работы и задачи, где вы исполнитель.',

  // ─── Поиск, отборы и заведение задачи — Ф4 · В-ПР-2а (Р-29, Р-31) ────────

  /**
   * Кнопка заведения задачи (поправка 2 к Р-29 — «кнопка стоит на ОБОИХ
   * экранах»). Подпись СОВПАДАЕТ с `taskScreenRu.create` дословно и намеренно:
   * форма одна и та же (`task-form.modal.tsx`), а два имени одного действия
   * читались бы как два разных действия. Ключ всё же свой, а не импорт
   * соседнего: словари экранов в этом файле не ссылаются друг на друга, и
   * «Поручения» вправе переименовать свою кнопку, не переименовав чужую.
   */
  create: 'Новая задача',
  /**
   * Запасной текст отказа ДЕЙСТВИЯ — Ф4 · фикс-3 (дверь постановщику, Р-20).
   *
   * ⚠️ ЭТО НЕ «ошибка загрузки», И РАЗВЕСТИ ИХ ОБЯЗАТЕЛЬНО: заголовок отказа
   * выдачи стоит на том же экране («Не удалось загрузить вашу работу») и
   * отвечает на другой вопрос. Здесь — про нажатую кнопку.
   *
   * ⚠️ И ЭТО ИМЕННО ЗАПАСНОЙ ТЕКСТ. Первым показывается СЕРВЕРНОЕ сообщение: у
   * самого частого отказа снятия (Р-34, 422 `WORK_DONE_CANNOT_ARCHIVE`) оно
   * называет выход дословно, и заменить его общей фразой значило бы отнять у
   * человека ответ.
   */
  actionFailed: 'Не удалось выполнить действие',
  /**
   * ⚠️ ПОДПИСЬ БЫЛА УЖЕ ПОВЕДЕНИЯ — ПОЧИНКА ПО **Р-37**.
   *
   * Стояло «Поиск по названию». После Р-37 поиск сверяет **видимое имя
   * строки**: название, если оно есть, **иначе** имя раздела — потому что
   * именно его рисует заголовком карточка и строка списка
   * (`taskCardTitle` / `workTitle`), когда `title` пуст. Подпись, обещающая
   * МЕНЬШЕ, чем делает поиск, — тот же класс, что комментарий, переживший
   * решение: человек не станет искать по разделу, потому что ему сказали, что
   * так нельзя. Дефект тихий — экран честно говорит «под отбор ничего не
   * подошло», и текст верен про отбор и ложен про предмет.
   *
   * ⚠️ И НЕ ШИРЕ ПОВЕДЕНИЯ ТОЖЕ — ЭТО ВТОРАЯ ПОЛОВИНА, И ОНА НЕ ОЧЕВИДНА.
   * Действует УЗКОЕ прочтение Р-37 (фолбэк, а не плоское ИЛИ): работа, у которой
   * название ЕСТЬ, по имени своего раздела НЕ находится — на экране этого слова
   * нет. Поэтому подсказка говорит «если названия нет», а не «название или
   * раздел»: второе обещало бы поиск по слову, которого человек не видит.
   *
   * ⚠️ ПОДПИСЬ ОБЩАЯ У ДВУХ ЭКРАНОВ И ПРАВИТСЯ ВМЕСТЕ. Часть 2а переносила поиск
   * с доски на «Мою работу» — вместе с текстом; починка одного экрана оставила
   * бы соседний врать, а предмет у них один.
   */
  searchLabel: 'Поиск по названию строки',
  searchPlaceholder: 'Название или раздел, если названия нет',
  /**
   * ⚠️ ПОДПИСЬ ОТБОРА — «Объект», А НЕ «Проект». Слово выбрано не здесь:
   * решения Блока 2 называют сущность объектом (Р-31 — «фильтр по объекту»), и
   * экран, сказавший «Проект», развёл бы словарь продукта с языком решений.
   */
  projectFilterLabel: 'Объект',
  /**
   * ⚠️ СТОРОЖ СПИСКА — СТАТИЧЕСКИЙ, ЗНАЧЕНИЯ — ИЗ СТРОК. Правило пережило снятый
   * Р-36 пункт «Без объекта» и записано про КЛАСС: обязательный элемент списка,
   * собранный из данных, исчезает ровно там, где он нужнее всего — у человека,
   * у которого таких строк сейчас нет.
   */
  projectFilterAll: 'Все объекты',
  sectionFilterLabel: 'Раздел',
  sectionFilterAll: 'Все разделы',
  /**
   * Пусто ПОД ОТБОРОМ — не то же, что пусто вообще (§7.23 плана В-ПР).
   *
   * ⚠️ ЭТО ПОЧИНКА КЛАССА, УЖЕ ОДНАЖДЫ ПОЙМАННОГО НА ЭТОМ ЭКРАНЕ. После
   * переезда отбора на сервер выдача приходит УЖЕ суженной, и ветка «ничего не
   * назначено» сказала бы «вам ничего не назначено» человеку, который просто
   * выбрал объект без строк. Тот же текст ушёл бы в живую область — то есть
   * доступное имя произнесло бы неправду.
   */
  emptyFiltered: 'Под отбор ничего не подошло',
  emptyFilteredHint: 'Строки никуда не делись — измените условия отбора или очистите поиск.',
  /**
   * ⚠️ ТРЕТИЙ СЛУЧАЙ, А НЕ ВТОРОЙ: под отбором строки ЕСТЬ, но лежат в других
   * ячейках. Сказать здесь «под отбор ничего не подошло» было бы такой же
   * неправдой, как «вам ничего не назначено», — только в другую сторону.
   */
  emptyFilteredCellHint: 'Под отбор строки есть — числа на вкладках показывают, в каких ячейках.',
  /**
   * Что объявляет живая область при переключении вида или состояния.
   *
   * ⚠️ ЧИСЛО, А НЕ СПИСОК — тот же довод, что на `/tasks`: диктор иначе читает
   * все строки со шкалами заново при каждом переключении.
   *
   * ⚠️ НАЗЫВАЕТ ЯЧЕЙКУ, А НЕ ТОЛЬКО ЧИСЛО, И ЭТО ПОЧИНКА ДВУХ ДЕФЕКТОВ РАЗОМ
   * (фикс-пак В-МР, Д-2 + Д-4). Прежняя редакция говорила «Найдено 3 задачи», а
   * на пустой ячейке — «Ничего не найдено». Оба текста неверны:
   *   · ⚠️ «Ничего не найдено» — это «пусто ВООБЩЕ», то самое, против чего в
   *     этом же файле написан текст `emptyCell`. Зрячий читал «Задач в работе
   *     нет» и шёл на соседнюю вкладку; незрячему доставалось «ничего нет», то
   *     есть ровно прежняя ложь — вылечили одного и оставили другого;
   *   · ⚠️ «Найдено 3» на двух РАЗНЫХ ячейках одинакового размера давало
   *     ДОСЛОВНО ТОТ ЖЕ текст, а неизменившийся текст живая область не
   *     объявляет вовсе: переход «Задачи · В работе (3)» → «Задачи ·
   *     Завершённые (3)» проходил беззвучно. Нажал — и не услышал ничего.
   * Имя ячейки решает обе задачи: оно и различает соседние ячейки, и на пустой
   * говорит, ЧЕГО именно нет.
   */
  liveCell: (
    kind: keyof typeof myWorkKindTabRu,
    state: keyof typeof myWorkStateTabRu,
    n: number,
  ): string =>
    n === 0
      ? myWorkEmptyCellRu[kind][state]
      : `${myWorkKindTabRu[kind]} · ${myWorkStateTabRu[state]}: ${n}`,
} as const;

/**
 * Экран «Поручения» `/tasks` и форма задачи — Блок 2 · Ф4 · В-ПР-2б (Р-29…Р-31).
 *
 * ═══ 🚫 «ДВА ВИДА» — ПОДПИСИ ПЕРЕКЛЮЧАТЕЛЯ СНЯТЫ Р-29 ═══════════════════
 *
 * ⚠️ ОТМЕНА С НОВЫМ ОСНОВАНИЕМ, А НЕ СТИРАНИЕ (форма Р-5). Прежняя врезка
 * объясняла, зачем подписи `viewMine` / `viewAssigned` разведены: «два вида —
 * это два ИСТОЧНИКА данных, а не два фильтра (Р-19), и переключатель нельзя
 * дать прочитать как „те же строки, отсортированные иначе"». Довод был верен
 * ЦЕЛИКОМ — и снят вместе со своим предметом: переключателя больше нет.
 *
 * **Что действует.** Экран отвечает на ОДИН вопрос — «чего я жду от других», —
 * и подписи его больше ни с чем не спорят. Пять ключей ушли вместе с
 * переключателем и видом «Мои»: `heading` (заголовок читается из
 * `workNavRu.tasks` — см. врезку там), `viewMine`, `viewAssigned`,
 * `viewGroupLabel`, `emptyMine`, `emptyMineHint`.
 *
 * ⚠️ `emptyMine` СНЯТ, А `emptyAssigned` ОСТАЛСЯ — и это не симметрия, а
 * следствие: пустота «Поручений» законна (Р-29) и для рядового сотрудника
 * ЕДИНСТВЕННО ВОЗМОЖНА (поправка 3), поэтому текст ей нужен сильнее, чем
 * раньше.
 *
 * ⚠️ ПУСТОТА И ОТКАЗ — РАЗНЫЕ СОСТОЯНИЯ, и текстов у них два. Смешать их
 * значит соврать в одном из двух случаев: «задач пока нет» на упавшем запросе
 * отправляет человека заводить задачу вместо того, чтобы повторить загрузку.
 *
 * ⚠️ И ПУСТОТ ТЕПЕРЬ ТРИ, А НЕ ДВЕ (§7.23 плана В-ПР): «не поручал ничего»,
 * «под отбор ничего не подошло» и «во „Завершённых" пусто». После переезда
 * отбора на сервер выдача приходит УЖЕ суженной, и один текст на три случая врёт
 * в двух из них.
 */
export const taskScreenRu = {
  /**
   * 🚫 `heading` СНЯТ: заголовок экрана читается из `workNavRu.tasks`, оттуда же
   * читает подпись меню. Разбор — во врезке у самого ключа. Здесь остаётся
   * только подзаголовок.
   */
  subtitle: 'Задачи, которые вы поручили другим. Состояние двигается перетаскиванием.',
  /**
   * Р-51 — ПОДПИСЬ МЕНЯЕТСЯ ВМЕСТЕ С ВИДОМ ДОСКИ (граница 3 решения).
   *
   * ⚠️ БЕЗ ВТОРОЙ ПОДПИСИ ПЕРВАЯ СТАНОВИТСЯ НЕПРАВДОЙ, А НЕ НЕТОЧНОСТЬЮ. Во
   * «Всех поручениях» на доске лежат задачи, которых смотрящий НЕ поручал
   * никому: их завели его сотрудники сами себе. Строка «Задачи, которые вы
   * поручили другим» над таким списком — утверждение, пережившее правку,
   * которая сделала его ложным; самый частый дефект этого блока.
   */
  subtitleAll:
    'Задачи, которые поручили вы, и те, что ваши сотрудники завели сами себе. Состояние двигается перетаскиванием.',
  /**
   * Р-51 — два вида доски.
   *
   * ⚠️ ТРИ СНЯТИЯ ПРАВИЛА ЛЕКСЕМЫ — ПО ТОМУ ЖЕ ОСНОВАНИЮ, ЧТО У `workNavRu.tasks`
   * строкой 426: правило запрещает «поручен…» как имя ВИДА РАБОТЫ (её заказчик
   * переименовал в «задачу» 07.08.2026), а здесь этим словом назван ЭКРАН —
   * его имя дал Р-29, и оно действующее. Снятие точечное и с причиной; общий
   * пропуск файла был бы обходом правила, а не исполнением решения.
   */
  // eslint-disable-next-line aris/no-legacy-lexeme -- Р-29/Р-51: имя ЭКРАНА, а не WorkKind.TASK
  scopeMine: 'Мои поручения',
  // eslint-disable-next-line aris/no-legacy-lexeme -- Р-29/Р-51: имя ЭКРАНА, а не WorkKind.TASK
  scopeAll: 'Все поручения',
  // eslint-disable-next-line aris/no-legacy-lexeme -- Р-29/Р-51: имя ЭКРАНА, а не WorkKind.TASK
  scopeFilterLabel: 'Чьи поручения показывать',
  create: 'Новая задача',
  /** Действия карточки задачи. */
  edit: 'Править',
  archive: 'Снять',
  restore: 'Вернуть',
  /** Подпись «снята» на карточке — из словаря, а не литералом в разметке. */
  archivedMark: 'снята',
  /**
   * ⚠️ ОДНА ПОДПИСЬ НА ОБА СОСТОЯНИЯ ПЕРЕКЛЮЧАТЕЛЯ. У кнопки с `aria-pressed`
   * доступное имя обязано быть ПОСТОЯННЫМ: меняющееся имя диктор объявляет как
   * появление другого элемента, а не как смену состояния одного. Прежняя пара
   * «Показать снятые» / «Скрыть снятые» именно это и делала.
   */
  archivedToggle: 'Снятые',
  /**
   * ⚠️ ПОДПИСЬ ПОЧИНЕНА ПО **Р-37** — разбор целиком в `myWorkRu.searchLabel`,
   * и он здесь не дублируется намеренно: две копии одного довода разойдутся на
   * первой же правке, и разойдутся молча.
   *
   * ⚠️ ТЕКСТ СОВПАДАЕТ С СОСЕДНИМ ЭКРАНОМ ДОСЛОВНО, А КЛЮЧ СВОЙ — та же
   * конвенция, что у кнопки заведения: словари экранов друг на друга не
   * ссылаются, но ОДИН предмет обязан называться одинаково. Поиск, обещающий на
   * двух экранах разное, читался бы как два разных поиска.
   *
   * ⚠️ У ЗАДАЧИ БЕЗ РАЗДЕЛА ФОЛБЭКУ НЕКУДА ПАДАТЬ — она находится только по
   * названию (граница Р-37). Подсказка это не нарушает: «если названия нет» не
   * обещает, что раздел найдётся всегда.
   */
  searchLabel: 'Поиск по названию строки',
  searchPlaceholder: 'Название или раздел, если названия нет',
  statusFilterLabel: 'Состояние',
  statusFilterAll: 'Любое состояние',

  // ─── Три отбора Р-31 — Ф4 · В-ПР-2б ─────────────────────────────────────

  /**
   * ⚠️ ОТБОР ПО СОТРУДНИКУ ЕСТЬ ТОЛЬКО ЗДЕСЬ (Р-31, граница «только на
   * „Поручениях", и это не экономия»). На «Моей работе» все строки принадлежат
   * одному человеку — тому, кто смотрит, — и там тот же отбор имел бы ровно одно
   * значение. Асимметрия названа прямо, потому что «сделать одинаково на обоих
   * экранах» выглядит аккуратнее и придёт в голову первым.
   */
  assigneeFilterLabel: 'Сотрудник',
  /**
   * ⚠️ СТОРОЖ СПИСКА — СТАТИЧЕСКИЙ, ЗНАЧЕНИЯ — ИЗ СТРОК. Правило записано про
   * КЛАСС, а не про снятый Р-36 пункт «Без объекта»: обязательный элемент
   * списка, собранный из данных, исчезает ровно там, где он нужнее всего — у
   * человека, у которого таких строк сейчас нет.
   */
  assigneeFilterAll: 'Все сотрудники',
  /**
   * ⚠️ ПОДПИСЬ ОТБОРА — «Объект», А НЕ «Проект». Слово выбрано не здесь: решения
   * Блока 2 называют сущность объектом (Р-31 — «фильтр по объекту»), и экран,
   * сказавший «Проект», развёл бы словарь продукта с языком решений. Совпадает с
   * `myWorkRu.projectFilterLabel` дословно и намеренно: один отбор на двух
   * экранах, названный по-разному, читается как два разных отбора.
   */
  projectFilterLabel: 'Объект',
  projectFilterAll: 'Все объекты',
  sectionFilterLabel: 'Раздел',
  sectionFilterAll: 'Все разделы',

  // ─── Доска и «Завершённые» — Р-30 ───────────────────────────────────────

  tabGroupLabel: 'Что показать',
  tabBoard: 'Доска',
  /**
   * ⚠️ ВКЛАДКА НАЗЫВАЕТСЯ ТАК ЖЕ, КАК ЯЧЕЙКА «Моей работы»
   * (`myWorkStateTabRu.done`), и это не совпадение: предмет один — закрытая
   * работа, — и два имени у него читались бы как две разные сущности.
   */
  tabCompleted: 'Завершённые',
  /**
   * Вкладка пуста — но экран НЕ пуст, и текст обязан это различать.
   *
   * ⚠️ «Задач нет» здесь было бы ложью: на доске они есть, просто закрыты
   * недавно (и потому лежат в колонке «Готово») или не закрыты вовсе. Та же
   * беда, что вылечена на «Моей работе» текстом пустой ЯЧЕЙКИ, только пришедшая
   * с другой стороны.
   */
  emptyCompleted: 'Здесь пока пусто',
  emptyCompletedHint:
    'Сюда уходят задачи, закрытые больше двух недель назад. Свежие остаются в колонке «Готово».',

  /**
   * Пустой экран — ЗАКОННОЕ состояние (Р-29), а у рядового сотрудника
   * ЕДИНСТВЕННО ВОЗМОЖНОЕ (поправка 3): назначать другим ему нельзя (Р-16), а
   * задачи себе живут на «Моей работе» (поправка 1). Пункта меню он не видит, но
   * по прямой ссылке экран обязан открыться и сказать это словами — не 403 и не
   * вид поломки.
   */
  emptyAssigned: 'Вы пока никому не поручали задач',
  /**
   * 🚫 ⚠️ «ИЛИ СЕБЕ» ИЗ ПОДСКАЗКИ УБРАНО, И ЭТО ПОЧИНКА, А НЕ СОКРАЩЕНИЕ.
   * Прежний текст обещал: «задачи, которые вы поставили СЕБЕ или сотрудникам».
   * После поправки 1 к Р-29 задача самому себе сюда не приходит ВОВСЕ — сервер
   * добавляет `assigneeId: { not: actor.id }`. Обещание, которое экран не может
   * исполнить, хуже отсутствующего: человек заведёт задачу себе, не увидит её
   * здесь и решит, что экран сломан.
   */
  emptyAssignedHint: 'Здесь появятся задачи, которые вы поставили сотрудникам.',
  /**
   * Пусто ПОД ОТБОРОМ — не то же, что «вы никому ничего не поручали» (§7.23).
   *
   * ⚠️ ЭТО ПОЧИНКА КЛАССА, УЖЕ ПОЙМАННОГО НА СОСЕДНЕМ ЭКРАНЕ. После переезда
   * отбора на сервер выдача приходит УЖЕ суженной, и ветка «не поручали»
   * сказала бы неправду человеку, который просто выбрал сотрудника без строк.
   * Тот же текст ушёл бы в живую область — то есть доступное имя произнесло бы
   * ложь.
   */
  emptyFiltered: 'Под отбор ничего не подошло',
  /** ⚠️ НАЗЫВАЕТ ВСЕ ПЯТЬ ОТБОРОВ, а не два: поиск, состояние, сотрудник, объект, раздел. */
  emptyFilteredHint: 'Строки никуда не делись — измените условия отбора или очистите поиск.',
  /**
   * ⚠️ ТЕКСТ НЕ НАЗЫВАЕТ ЭКРАН ПО ИМЕНИ, И ЭТО НЕ ОБЕДНЕНИЕ. «Не удалось
   * загрузить поручения» уперлось бы в лексический гейт (`aris/no-legacy-lexeme`),
   * а послабление под отказ загрузки не стоит того: имя экрана человек и так
   * видит в заголовке строкой выше, а сообщение обязано говорить о ДЕЙСТВИИ —
   * «список не приехал, повторите», — а не повторять, где он находится.
   */
  loadFailed: 'Не удалось загрузить список',
  /**
   * Что объявляет живая область при смене фильтра.
   *
   * ⚠️ ЧИСЛО, А НЕ СПИСОК. Живая область на самом списке заставляла диктора
   * прочитать все карточки со шкалами и кнопками при переходе «5 → 3»; человеку
   * нужно знать, СКОЛЬКО нашлось, а не слушать выдачу заново.
   */
  foundCount: (n: number) =>
    n === 0 ? 'Ничего не найдено' : `Найдено ${n} ${pluralRu(n, ['задача', 'задачи', 'задач'])}`,
} as const;

/**
 * Доска задач с перетаскиванием — Блок 2 · Ф4 · В6-в (Р-19).
 *
 * ⚠️ ПОДПИСЕЙ КОЛОНОК ЗДЕСЬ НЕТ, И ЭТО НЕ ПРОПУСК. Колонка называется своим
 * состоянием, а подпись состояния живёт в ЕДИНСТВЕННОМ словаре
 * (`WORK_STATUS_LABEL` из `@aris/shared`, псевдоним `workStatusRu`). Второй
 * набор подписей разошёлся бы со шкалой молча — и первая колонка называлась бы
 * «Бэклог» там, где на шкале написано «Не начато».
 *
 * ⚠️ ТЕКСТОВ ОТКАЗА ЗДЕСЬ ТОЖЕ НЕТ. Причину, по которой колонка не принимает
 * карточку, доска берёт у `denialReason` — единственной реализации правила.
 */
export const taskBoardRu = {
  /** Колонка пуста — это нормальное состояние, а не ошибка. */
  emptyColumn: 'Пусто',
  /**
   * Строку изменил кто-то другой, пока её тащили (409 `VERSION_CONFLICT`).
   *
   * ⚠️ МОЛЧАЛИВЫЙ ОТКАТ ЧИТАЕТСЯ КАК ПОЛОМКА. Карточка возвращается на прежнее
   * место сама; без объяснения это выглядит как «доска глючит», и человек
   * повторяет жест ещё дважды, прежде чем позвать.
   *
   * 🚫 ⚠️ ПРЕЖНЯЯ РЕДАКЦИЯ («список обновлён») БЫЛА ЛОЖЬЮ И СНЯТА С НОВЫМ
   * ОСНОВАНИЕМ. Выдача тогда не перезапрашивалась вовсе: сброс ключей висел
   * только на успехе, а боевой клиент держит `staleTime: 30_000` и не
   * перечитывает по возврату фокуса. Человек, послушавшийся инструкции,
   * повторял перенос с ТОЙ ЖЕ версией и получал второй 409 — отказ по кругу.
   * Перезапрос заведён; текст говорит ровно то, что произошло.
   */
  conflict: 'Задачу изменил кто-то другой — список перезапрошен, повторите перенос',
  /**
   * В колонке есть строки, которых актору менять нельзя, — уплотнить её он не
   * может.
   *
   * ⚠️ ТЕКСТ СВОЙ, А НЕ ПРИЧИНА ОТКАЗА СОСЕДНЕЙ СТРОКИ, И ЭТО НЕ ВТОРОЕ
   * ПРАВИЛО. Правило одно и спрашивается у `denialReason`; здесь называется
   * ОПЕРАЦИЯ, которая от него пострадала. Подставив причину соседа, экран
   * сказал бы «завершить задачу может только тот, кто её поставил» человеку,
   * который ничего не завершал, — и отправил бы его искать несуществующую свою
   * ошибку.
   *
   * ⚠️ И ЭТО ШТАТНЫЙ СОСТАВ КОЛОНКИ, А НЕ РЕДКОСТЬ: в виде «мои задачи»
   * «Готово» по Р-18 состоит из задач, закрытых постановщиком.
   */
  compactBlocked:
    'В этой колонке есть задачи, которые вам менять нельзя, — порядок в ней не перестроить',
  /**
   * Колонка упёрлась в верхнюю границу типа — уплотнять некуда.
   *
   * Достижимо только искусственными данными, но молчать нельзя: жест иначе
   * просто ничего не сделает.
   */
  compactImpossible: 'Не удалось пересчитать порядок в колонке — обновите страницу и повторите',
  /**
   * Жест кончился ничем — карточка там же, где была.
   *
   * ⚠️ ЭТО НЕ ШУМ, А СИММЕТРИЯ КАНАЛОВ, И НАЙДЕНА ОНА НА СТЕНДЕ. Отказ и
   * ХОЛОСТОЙ жест выглядят одинаково: карточка отпрыгивает назад. Отказ
   * объяснялся тостом, холостой жест не объяснялся ничем — и человек, не
   * различая их, читал молчание как поломку доски.
   *
   * ⚠️ И ТРЕТИЙ ДОВОД, РЕШАЮЩИЙ: незрячему про холостой жест УЖЕ говорили —
   * `a11y.stayed` объявляет «осталась на месте» с самого круга объявлений.
   * Зрячий получал МЕНЬШЕ сведений, чем экранный диктор; асимметрию завёл я,
   * и снимается она здесь.
   */
  noMove: 'Карточка осталась на месте',
  /**
   * Речь для экранного диктора.
   *
   * ⚠️ ПО УМОЛЧАНИЮ БИБЛИОТЕКА ГОВОРИТ ПО-АНГЛИЙСКИ. Своих сообщений она не
   * требует, и именно поэтому их забывают: доска выглядит рабочей, а диктор
   * посреди русского интерфейса читает «Draggable item was dropped over
   * droppable area».
   */
  a11y: {
    roleDescription: 'перетаскиваемая карточка',
    handleLabel: (title: string) => `Перетащить: ${title}`,
    instructions:
      'Нажмите пробел или Enter, чтобы взять карточку. Стрелками выберите место, ' +
      'пробелом опустите, Escape отменит перенос.',
    lifted: (title: string) => `Карточка «${title}» взята.`,
    over: (title: string, zone: string) => `Карточка «${title}» над колонкой «${zone}».`,
    outside: (title: string) => `Карточка «${title}» вне колонок.`,
    dropped: (title: string, zone: string) => `Карточка «${title}» опущена в колонку «${zone}».`,
    /** Жест кончился ничем — карточка там же, где была. */
    stayed: (title: string) => `Карточка «${title}» осталась на месте.`,
    /** Правило не пустило; причина приходит отдельной строкой, из `denialReason`. */
    refused: (title: string) => `Карточку «${title}» переставить не удалось.`,
    /**
     * Какие колонки этой карточке закрыты — говорится СРАЗУ ПРИ ВЗЯТИИ.
     *
     * ⚠️ У КЛАВИАТУРЫ НЕТ КАНАЛА, КОТОРЫЙ ЕСТЬ У ГЛАЗА. Зрячий видит погашенную
     * колонку и причину под её заголовком; нажатие стрелки в сторону запертой
     * колонки не делает и не объявляет НИЧЕГО — библиотека убрала зону из
     * выбора. Молчание в ответ на нажатие читается как поломка.
     */
    lockedColumns: (zones: readonly string[], reason: string) =>
      `${zones.length === 1 ? 'Колонка' : 'Колонки'} ${zones
        .map((z) => `«${z}»`)
        .join(', ')} закрыт${zones.length === 1 ? 'а' : 'ы'}: ${reason}`,
    cancelled: (title: string) => `Перенос карточки «${title}» отменён.`,
  },
} as const;

/**
 * Форма задачи — Блок 2 · Ф4 · В6-б.
 *
 * ⚠️ ОБЪЕКТ ОБЯЗАТЕЛЕН НА ЭКРАНЕ, ХОТЯ СЕРВЕР ПУТЬ «БЕЗ ОБЪЕКТА» ОСТАВЛЯЕТ
 * (Р-9: непроектная работа падает на служебный объект). Форма закрывает его
 * сама, и текст отказа обязан звать выбрать объект, а не сообщать о поломке.
 */
export const taskFormRu = {
  createTitle: 'Новая задача',
  editTitle: 'Правка задачи',
  createDescription: 'Задача попадёт в загрузку исполнителя по своему периоду.',
  editDescription: 'Период, исполнителя и оценку правит тот, кто задачу поставил.',
  projectLabel: 'Объект',
  projectPlaceholder: 'Выберите объект',
  /**
   * ⚠️ ТЕКСТ ОТКАЗА НЕ ПОВТОРЯЕТ ПОДСКАЗКУ ПОЛЯ, И ЭТО НЕ СТИЛЬ. Совпади они —
   * «отказ виден на экране» стало бы непроверяемым: подсказка стоит в поле
   * ВСЕГДА, то есть утверждение было бы зелено и до нажатия.
   */
  projectRequired: 'Укажите объект',
  projectSearchPlaceholder: 'Название или шифр',
  projectEmpty: 'Подходящих объектов нет',
  titleLabel: 'Название',
  titlePlaceholder: 'Что нужно сделать',
  titleRequired: 'Укажите название',
  titleTooLong: 'Название не должно превышать 300 символов',
  descriptionLabel: 'Описание',
  descriptionTooLong: 'Описание не должно превышать 2000 символов',
  assigneeRequired: 'Выберите исполнителя',
  startRequired: 'Укажите дату начала',
  dueRequired: 'Укажите срок',
  saveFailed: 'Не удалось сохранить задачу',
  hoursLabel: 'Оценка часов',
  hoursHint: 'Необязательно. Без оценки план по задаче не считается — только период.',
  /**
   * ⚠️ ТЕКСТ ОБЯЗАН НАЗЫВАТЬ ДЕЙСТВУЮЩЕЕ ПРАВИЛО, А НЕ СОСЕДНЕЕ.
   *
   * 🚫 Прежняя редакция — «от 0,5 до 999,5 с шагом 0,5» — врала ТРИЖДЫ: правило
   * контракта (`HoursEstimateSchema`) это шаг 0,1, потолок 9999,9 и нижняя
   * граница «больше нуля». Прогоном проверено: 0,3 / 2,7 / 1234,5 проходят.
   * Похоже на перенос из окна списания часов, где правило действительно
   * 0,5–24, — и цена переноса не косметическая: человек с честной оценкой 2,7
   * округлил бы её до 2,5 по подсказке интерфейса и испортил бы цифру, которая
   * по Р-17 и есть план задачи.
   */
  hoursInvalid: 'Оценка — положительное число с точностью до десятых, не больше 9999,9',
  /** Вид работы = раздел служебного объекта (Р-10); подпись — `sectionLabel`. */
  kindRequired: 'Выберите вид работы',
  /** ⚠️ Ф5 · В9-b: переэкспорт из общего пакета — вторая половина пары
   *  «Вид работы» / «Раздел» (**Р-70, граница 6**). Разбор — у
   *  `SECTION_FIELD_LABEL` в `@aris/shared`. */
  sectionLabel: SECTION_FIELD_LABEL.regular,
  sectionPlaceholder: 'Выберите раздел',
  sectionEmpty: 'У объекта нет разделов',
  sectionLoading: 'Загружаем разделы…',
  /**
   * ⚠️ ПЕРЕНОСА ЗАДАЧИ НЕ СУЩЕСТВУЕТ, И ЭТО РЕШЕНИЕ, А НЕ ПРОПУСК В ФОРМЕ:
   * `UpdateWorkRequestSchema` не несёт ни `projectId`, ни `sectionId`, и там же
   * записано почему. Текст говорит, ЧТО ДЕЛАТЬ вместо переноса, — иначе
   * запертое поле читается как поломка.
   */
  noMove: 'Объект и раздел у заведённой задачи не меняются: снимите её и заведите заново',
  /**
   * ⚠️ СЛУЖЕБНОГО ОБЪЕКТА МОЖЕТ НЕ БЫТЬ ВОВСЕ (§3.2 плана фазы). Форма обязана
   * отвечать ВНЯТНЫМ отказом, а не пустым пикером: пустой пикер читается как
   * поломка данных, и чинить пойдут не туда.
   */
  internalMissing:
    'Служебный объект «Внутренние работы» не найден. ' +
    'Непроектную задачу завести некуда — сообщите администратору.',
  submitCreate: 'Завести задачу',
  submitEdit: 'Сохранить',
  cancel: 'Отмена',
  createdToast: 'Задача заведена',
  savedToast: 'Задача сохранена',
  /**
   * ⚠️ ПУСТАЯ ПРАВКА ЗАКРЫВАЕТСЯ БЕЗ ЗАПРОСА — и без этого текста «Сохранить»
   * становится неотличимо от «Отмена»: человек не узнаёт, сохранилось ли.
   * Запроса при этом действительно нет: он завёл бы строку аудита «поменяли
   * ничего» и поднял версию строки на ровном месте.
   */
  noChangesToast: 'Изменений нет',
  /**
   * Р-105 — нижняя граница начала у задачи САМОМУ СЕБЕ: неделя назад. Текст
   * называет правило, а не «дата неверна»: человек обязан понять, что
   * ограничение относится именно к задаче себе, и что задача сотруднику его
   * не касается.
   *
   * 🚫 ⚠️ БЫЛО СТРОКОЙ: «Задачу себе нельзя начать в прошлом — выберите
   * сегодняшнюю дату или позже» (Р-21). После **Р-105** (29.08.2026) это
   * ПРЯМАЯ ЛОЖЬ — прошлое разрешено на неделю вглубь, — поэтому текст сменён
   * вместе с порогом.
   *
   * ⚠️ ФУНКЦИЯ, А НЕ СТРОКА, И ТЕЛО ЕЁ — В `@aris/shared`. Отказ обязан
   * называть КОНКРЕТНУЮ дату границы, иначе человеку остаётся считать неделю
   * в уме; а сам текст — один с серверным, потому что два текста одного
   * правила однажды разойдутся, и разойдутся молча (тот же довод, что у
   * `PERIOD_MESSAGE`).
   */
  selfTaskBackdated: (floorIso: string): string => selfTaskBackdatedMessage(floorIso),
} as const;

/** Виды отсутствий. Буква — ВТОРОЙ канал различения, помимо цвета. */
export const absenceKindRu = {
  VACATION: { letter: 'О', label: 'Отпуск' },
  SICK_LEAVE: { letter: 'Б', label: 'Больничный' },
} as const;

/**
 * Полный словарь Блока 2 одним объектом — предпочтительная форма импорта:
 *
 *   import { workRu } from '@/i18n';
 *   workRu.status.ON_REVIEW; // «На проверке»
 */
export const workRu = {
  kind: workKindRu,
  kindPlural: workKindPluralRu,
  status: workStatusRu,
  flag: workFlagRu,
  dates: workDatesRu,
  primitives: workPrimitivesRu,
  internal: internalWorkRu,
  nav: workNavRu,
  hierarchy: workHierarchyRu,
  gantt: ganttRu,
  absenceKind: absenceKindRu,
  /** Ф4 · В-МР — личный экран «Моя работа» (Р-28). */
  myWork: myWorkRu,
  /** Ф4 · В6-б — экран задач и форма задачи. */
  task: taskScreenRu,
  taskForm: taskFormRu,
  /** Ф4 · В6-в — доска задач с перетаскиванием. */
  board: taskBoardRu,
} as const;

export type WorkKind = keyof typeof workKindRu;
export type WorkStatus = keyof typeof workStatusRu;
export type WorkRuStrings = typeof workRu;
